import React, { useState, useEffect } from 'react';
import { CheckCircle, Calendar, User, Mail, Phone, Shield, Clock, FileText, DollarSign, Award, AlertCircle } from 'lucide-react';

interface TaxPreparationFormProps {
  onSubmit?: (data: any) => void;
  onPreview?: boolean;
}

const TaxPreparationForm: React.FC<TaxPreparationFormProps> = ({ onSubmit, onPreview = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredTime: '',
    isOver55: false,
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    if (!formData.preferredTime) {
      newErrors.preferredTime = 'Please select a preferred contact time';
    }
    
    if (!formData.isOver55) {
      newErrors.isOver55 = 'You must be 55 or older to qualify for this offer';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the form data to your backend
      console.log('Form submitted:', formData);
      
      if (onSubmit) {
        onSubmit(formData);
      }
      
      setSubmitted(true);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 bg-white">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Claim Your $99 Tax Preparation Offer
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-lg font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-6 w-6 text-gray-400" />
            </div>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`pl-12 block w-full rounded-lg border ${errors.name ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
              placeholder="John Smith"
            />
          </div>
          {errors.name && (
            <p className="mt-2 text-red-600">{errors.name}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-lg font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-6 w-6 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`pl-12 block w-full rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && (
            <p className="mt-2 text-red-600">{errors.email}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-lg font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-6 w-6 text-gray-400" />
            </div>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`pl-12 block w-full rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
              placeholder="(555) 123-4567"
            />
          </div>
          {errors.phone && (
            <p className="mt-2 text-red-600">{errors.phone}</p>
          )}
        </div>
        
        <div>
          <label htmlFor="preferredTime" className="block text-lg font-medium text-gray-700 mb-2">
            Preferred Contact Time
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Clock className="h-6 w-6 text-gray-400" />
            </div>
            <select
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              className={`pl-12 block w-full rounded-lg border ${errors.preferredTime ? 'border-red-300' : 'border-gray-300'} py-4 text-gray-900 shadow-sm focus:border-primary focus:ring-primary text-lg`}
            >
              <option value="">Select a time</option>
              <option value="morning">Morning (9AM - 12PM)</option>
              <option value="afternoon">Afternoon (12PM - 5PM)</option>
              <option value="evening">Evening (5PM - 8PM)</option>
            </select>
          </div>
          {errors.preferredTime && (
            <p className="mt-2 text-red-600">{errors.preferredTime}</p>
          )}
        </div>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex items-center h-6">
              <input
                id="isOver55"
                name="isOver55"
                type="checkbox"
                checked={formData.isOver55}
                onChange={handleChange}
                className={`h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary ${errors.isOver55 ? 'border-red-300' : ''}`}
              />
            </div>
            <div className="ml-3">
              <label htmlFor="isOver55" className="text-lg font-medium text-gray-700">
                I am 55 years of age or older
              </label>
            </div>
          </div>
          {errors.isOver55 && (
            <p className="mt-1 text-red-600">{errors.isOver55}</p>
          )}
          
          <div className="flex items-start">
            <div className="flex items-center h-6">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className={`h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary ${errors.agreeToTerms ? 'border-red-300' : ''}`}
              />
            </div>
            <div className="ml-3">
              <label htmlFor="agreeToTerms" className="text-base text-gray-700">
                I agree to the terms and conditions
              </label>
            </div>
          </div>
          {errors.agreeToTerms && (
            <p className="mt-1 text-red-600">{errors.agreeToTerms}</p>
          )}
        </div>
        
        <button
          type="submit"
          className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 px-4 rounded-lg text-xl transition-colors duration-200 shadow-md hover:shadow-lg"
        >
          Claim My $99 Special Offer
        </button>
        
        <p className="text-center text-gray-500 text-sm mt-4">
          By submitting this form, you agree to be contacted about tax preparation services.
        </p>
      </form>
    </div>
  );
};

export default TaxPreparationForm;